import BaseAiService from '@src/slide/application/service/ai/core/base/base-ai-service';
import { ESource, ShowOptions } from '@tencent/ai-sdk-interface';
import type { AIPCController } from '@tencent/ai-sidebar-sdk';
import { IReactionDisposer, reaction } from 'mobx';
import { IPcAiService, IPcAIServiceDelegates } from '@src/slide/application/service/ai/core/pc/pc-ai-service.interface';
import { getAiAssistant } from '@src/js/common/ark-extensions/dc-components/ai-assistant-pc';
import { SlideAgent } from './agent';
import { GradPalette, createSolidPalette } from './task/background-fill/components/palette-card';
import { BackgroundFillTask } from './task/background-fill';
import { tipsCard } from './components/tips-card/tips-card';
import { getSearchImageCard } from './components/search-images/search-images';

export default class PcAiService extends BaseAiService implements IPcAiService {
    public aiSidebarController: AIPCController;
    protected declare services: IPcAIServiceDelegates;

    private editorReaction?: IReactionDisposer;

    public constructor() {
        super();
    }

    public async toggleSidebar(options?: ShowOptions) {
        await this.aiControllerLoader;
        if (this.aiSidebarController.isVisible) {
            this.hideSideBar();
        } else {
            this.showSideBar(options);
        }
    }

    public async showSideBar(options?: ShowOptions) {
        await this.aiControllerLoader;
        this.aiSidebarController.show({ tabIndex: -1, ...options });
        this.attachListener();
    }

    public async hideSideBar() {
        await super.hideSideBar();
        this.removeListener();
    }

    protected async createAIController(): Promise<AIPCController> {
        const aiAssistant = await getAiAssistant();
        this.registerDocsApi(aiAssistant.api);

        const controller = aiAssistant.initialize({
            context: { clientVars: {} },
            source: ESource.SOURCE_SLIDE,
            lifecycles: {
                onShow: () => {
                    this.aiLifeCyclesFunction?.onShow?.();
                },
                onHide: () => {
                    this.aiLifeCyclesFunction?.onHide?.();
                },
            },
        });

        const backgroundFillTask = new BackgroundFillTask(
            this.services.uiDataService,
            this.services.selectionService,
            this.services.themeColorManager,
        );

        controller.registerCustomAgents([
            new SlideAgent(
                backgroundFillTask,
                this,
                this.services.uiDataService,
                this.services.masterLayoutEditService,
                this.services.slideSizeService,
            ),
        ]);

        const aiReplaceImageService = await this.services.aiReplaceImageService.promise();
        controller.registerCustomTaskCards({
            'solid-palette': createSolidPalette(backgroundFillTask),
            'grad-palette': GradPalette,
            'slide-tips': tipsCard,
            'slide-search-images': getSearchImageCard(aiReplaceImageService),
        });
        return controller;
    }

    // 监听编辑态侧边栏变化及演示态变化
    private attachListener() {
        // 设置格式面板、动画面板、进入演示态时、母版编辑都需要让AI助手消失
        this.editorReaction = reaction(
            () => ({
                isPropertyEditorShown: this.services?.rightSideBarManager?.isPropertyEditorShown,
                isAnimationEditorShown: this.services?.rightSideBarManager?.isAnimationEditorShown,
                isPlaying: this.services?.playManager?.isPlaying,
                isMasterEditType: this.services?.masterLayoutEditService?.isMasterEditType,
            }),
            ({ isPropertyEditorShown, isAnimationEditorShown, isPlaying, isMasterEditType }) => {
                if (isPropertyEditorShown || isAnimationEditorShown || isPlaying || isMasterEditType) {
                    this.hideSideBar();
                }
            },
        );

        this.getHistoryManager()?.on('show', this.onHistoryShow);
    }

    private onHistoryShow = () => {
        this.hideSideBar();
    };

    // 移除监听
    private removeListener() {
        this.editorReaction?.();
        this.getHistoryManager()?.off('show', this.onHistoryShow);
    }

    private getHistoryManager() {
        return this.services?.historyManager;
    }
}
